// src/widgets/widgets/WhatAreYouPaying.tsx
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
	Paper,
	Text,
	Stack,
	Group,
	TextInput,
	Badge,
	Progress,
	ThemeIcon,
	Select,
	Tooltip,
	Loader,
	Button,
	Alert,
} from '@mantine/core';
import {
	IconUsers,
	IconTrendingUp,
	IconTrendingDown,
	IconMinus,
	IconBuilding,
	IconInfoCircle,
	IconMapPin,
} from '@tabler/icons-react';
import type { WidgetProps, WidgetBillFormProps } from '../types';
import {
	getOfficialAverages,
	type OfficialCategoryAverages,
} from '../../services/officialRentData';

/**
 * Bill categories for comparison
 */
export const BILL_CATEGORIES = [
	{ value: 'rent', label: 'Rent' },
	{ value: 'utilities', label: 'Utilities' },
	{ value: 'insurance', label: 'Insurance' },
	{ value: 'phone', label: 'Phone' },
	{ value: 'internet', label: 'Internet' },
	{ value: 'other', label: 'Other' },
] as const;

export type BillCategory = (typeof BILL_CATEGORIES)[number]['value'];

/**
 * Data structure stored in bill.widgetData['what-are-you-paying']
 */
export interface WhatAreYouPayingData {
	shared: boolean;
	zipCode: string;
	category: BillCategory;
	/** User's term for this bill (e.g., "Mobile" instead of "Phone") */
	displayName?: string;
}

/**
 * Keyword mappings for auto-categorization
 * Maps lowercase keywords to bill categories
 */
const CATEGORY_KEYWORDS: Record<BillCategory, string[]> = {
	rent: [
		'rent',
		'lease',
		'mortgage',
		'housing',
		'apartment',
		'hoa',
		'homeowner',
	],
	utilities: [
		'electric',
		'electricity',
		'power',
		'gas',
		'water',
		'utility',
		'utilities',
		'sewage',
		'sewer',
		'trash',
		'garbage',
		'heating',
		'cooling',
		'hvac',
		'energy',
		'pge',
		'pg&e',
		'edison',
		'duke energy',
		'con ed',
		'coned',
	],
	insurance: [
		'insurance',
		'insur',
		'coverage',
		'policy',
		'premium',
		'geico',
		'state farm',
		'allstate',
		'progressive',
		'liberty mutual',
		'usaa',
	],
	phone: [
		'phone',
		'cell',
		'mobile',
		'cellular',
		'verizon',
		'at&t',
		'att',
		't-mobile',
		'tmobile',
		'sprint',
		'mint mobile',
		'cricket',
		'metro pcs',
		'metropcs',
	],
	internet: [
		'internet',
		'wifi',
		'wi-fi',
		'broadband',
		'cable',
		'fiber',
		'comcast',
		'xfinity',
		'spectrum',
		'cox',
		'fios',
		'frontier',
		'centurylink',
		'hughesnet',
		'starlink',
	],
	other: [],
};

/**
 * Detect bill category from bill name
 */
export function detectCategoryFromBillName(billName: string): BillCategory {
	if (!billName) return 'other';

	const lowerName = billName.toLowerCase();

	for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
		if (category === 'other') continue;

		for (const keyword of keywords) {
			if (lowerName.includes(keyword)) {
				return category as BillCategory;
			}
		}
	}

	return 'other';
}

/**
 * Validate widget data - returns error message or null
 * Note: ZIP code is no longer required here - users can add it on the widget
 */
export function validateWhatAreYouPayingData(
	data: Record<string, unknown>
): string | null {
	const widgetData = data as WhatAreYouPayingData;

	// If ZIP is provided, validate format
	if (widgetData.zipCode && widgetData.zipCode.length > 0) {
		if (widgetData.zipCode.length !== 5) {
			return 'ZIP code must be exactly 5 digits';
		}
		if (!/^\d{5}$/.test(widgetData.zipCode)) {
			return 'ZIP code must contain only numbers';
		}
	}

	return null;
}

/**
 * Extract a clean display name from the bill name
 * Removes common suffixes like "Bill", "Payment", etc.
 */
export function extractDisplayName(billName: string): string {
	if (!billName) return '';

	// Remove common suffixes
	const suffixes = [
		' bill',
		' payment',
		' monthly',
		' service',
		' services',
		' plan',
		' subscription',
	];

	let name = billName.trim();
	const lowerName = name.toLowerCase();

	for (const suffix of suffixes) {
		if (lowerName.endsWith(suffix)) {
			name = name.slice(0, -suffix.length).trim();
			break;
		}
	}

	// Capitalize first letter
	return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * Form section rendered in AddBillForm/EditBillModal
 */
export function WhatAreYouPayingFormSection({
	widgetData,
	onWidgetDataChange,
	billName,
}: WidgetBillFormProps) {
	const data = widgetData as WhatAreYouPayingData | undefined;
	const [zipCode, setZipCode] = useState(data?.zipCode || '');
	const [category, setCategory] = useState<string | null>(
		data?.category || 'other'
	);
	const [displayName, setDisplayName] = useState(
		data?.displayName || (billName ? extractDisplayName(billName) : '')
	);
	const [hasAutoDetected, setHasAutoDetected] = useState(false);

	// Auto-detect category from bill name on initial load
	useEffect(() => {
		if (billName && !hasAutoDetected && (!data?.category || data.category === 'other')) {
			const detectedCategory = detectCategoryFromBillName(billName);
			if (detectedCategory !== 'other') {
				setCategory(detectedCategory);
				setHasAutoDetected(true);
			}
			// Also set display name from bill name
			if (!data?.displayName) {
				setDisplayName(extractDisplayName(billName));
			}
		}
	}, [billName, hasAutoDetected, data?.category, data?.displayName]);

	// Sync state changes to parent
	useEffect(() => {
		onWidgetDataChange({
			shared: true,
			zipCode,
			category: category || 'other',
			displayName: displayName || extractDisplayName(billName || ''),
		});
	}, [zipCode, category, displayName, billName, onWidgetDataChange]);

	const handleZipCodeChange = (value: string) => {
		// Only allow digits, max 5
		const cleaned = value.replace(/\D/g, '').slice(0, 5);
		setZipCode(cleaned);
	};

	const zipCodeError =
		zipCode.length > 0 && zipCode.length < 5
			? 'Enter 5 digits'
			: undefined;

	return (
		<Stack gap='xs'>
			<Select
				label='Bill Category'
				placeholder='Select category'
				data={BILL_CATEGORIES}
				value={category}
				onChange={setCategory}
				size='sm'
			/>
			<TextInput
				label='Your ZIP Code'
				placeholder='12345'
				description='For local comparisons (can add later)'
				value={zipCode}
				onChange={(e) => handleZipCodeChange(e.target.value)}
				maxLength={5}
				size='sm'
				error={zipCodeError}
				styles={{
					input: {
						width: 120,
					},
				}}
			/>
			<Text size='xs' c='dimmed'>
				Your data is anonymous and only used for aggregated comparisons.
			</Text>
		</Stack>
	);
}

/**
 * Main widget component shown on For You page
 */
function WhatAreYouPaying({ bills, onUpdateBill }: WidgetProps) {
	const [officialAverages, setOfficialAverages] =
		useState<OfficialCategoryAverages | null>(null);
	const [dataSource, setDataSource] = useState<'official' | 'national_fallback'>(
		'national_fallback'
	);
	const [dataYear, setDataYear] = useState<string | undefined>();
	const [isLoading, setIsLoading] = useState(false);
	const [currentZipCode, setCurrentZipCode] = useState<string>('');
	const [pendingZipCode, setPendingZipCode] = useState<string>('');

	// Get bills that are shared with this widget (complete - have ZIP)
	const sharedBills = useMemo(() => {
		return bills.filter((bill) => {
			const data = bill.widgetData?.[
				'what-are-you-paying'
			] as WhatAreYouPayingData | undefined;
			return data?.shared && data?.zipCode && data?.category;
		});
	}, [bills]);

	// Get bills that are shared but missing ZIP
	const billsMissingZip = useMemo(() => {
		return bills.filter((bill) => {
			const data = bill.widgetData?.[
				'what-are-you-paying'
			] as WhatAreYouPayingData | undefined;
			return data?.shared && !data?.zipCode;
		});
	}, [bills]);

	// Get the most common ZIP code from shared bills
	const primaryZipCode = useMemo(() => {
		const zipCounts: Record<string, number> = {};
		sharedBills.forEach((bill) => {
			const data = bill.widgetData?.[
				'what-are-you-paying'
			] as WhatAreYouPayingData;
			zipCounts[data.zipCode] = (zipCounts[data.zipCode] || 0) + 1;
		});

		let maxZip = '';
		let maxCount = 0;
		Object.entries(zipCounts).forEach(([zip, count]) => {
			if (count > maxCount) {
				maxZip = zip;
				maxCount = count;
			}
		});

		return maxZip;
	}, [sharedBills]);

	// Fetch official averages when ZIP code changes
	const fetchOfficialData = useCallback(async (zipCode: string) => {
		if (!zipCode || zipCode.length !== 5) return;

		setIsLoading(true);
		try {
			const result = await getOfficialAverages(zipCode);
			setOfficialAverages(result.averages);
			setDataSource(result.source);
			setDataYear(result.dataYear);
			setCurrentZipCode(zipCode);
		} catch (error) {
			console.error('Failed to fetch official averages:', error);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Fetch data when primary ZIP code changes
	useEffect(() => {
		if (primaryZipCode && primaryZipCode !== currentZipCode) {
			fetchOfficialData(primaryZipCode);
		}
	}, [primaryZipCode, currentZipCode, fetchOfficialData]);

	// Group by category and calculate stats
	const categoryStats = useMemo(() => {
		const stats: Record<
			string,
			{ total: number; count: number; bills: typeof sharedBills }
		> = {};

		sharedBills.forEach((bill) => {
			const data = bill.widgetData?.[
				'what-are-you-paying'
			] as WhatAreYouPayingData;
			const cat = data.category;

			if (!stats[cat]) {
				stats[cat] = { total: 0, count: 0, bills: [] };
			}
			stats[cat].total += bill.amount;
			stats[cat].count += 1;
			stats[cat].bills.push(bill);
		});

		return stats;
	}, [sharedBills]);

	// Fallback averages (national) if official data not loaded
	const fallbackAverages: OfficialCategoryAverages = {
		rent: 1450,
		utilities: 150,
		insurance: 120,
		phone: 85,
		internet: 65,
		other: 100,
	};

	const averages = officialAverages || fallbackAverages;

	if (sharedBills.length === 0) {
		return (
			<Paper withBorder p='md' radius='md'>
				<Group gap='sm' mb='sm'>
					<ThemeIcon size='lg' radius='md' variant='light' color='cyan'>
						<IconUsers size={20} />
					</ThemeIcon>
					<Text fw={600}>What Are You Paying</Text>
				</Group>

				{/* Show prompt if bills are shared but missing ZIP */}
				{billsMissingZip.length > 0 && onUpdateBill ? (
					<Stack gap='sm'>
						<Text size='sm' c='dimmed'>
							{billsMissingZip.length} bill{billsMissingZip.length !== 1 ? 's' : ''} ready to compare - just add your ZIP code:
						</Text>
						<Group gap='xs'>
							<TextInput
								placeholder='ZIP code'
								value={pendingZipCode}
								onChange={(e) => {
									const cleaned = e.target.value.replace(/\D/g, '').slice(0, 5);
									setPendingZipCode(cleaned);
								}}
								size='sm'
								maxLength={5}
								styles={{
									input: { width: 100 },
								}}
							/>
							<Button
								size='sm'
								disabled={pendingZipCode.length !== 5}
								onClick={() => {
									billsMissingZip.forEach((bill) => {
										const existingData = bill.widgetData?.[
											'what-are-you-paying'
										] as WhatAreYouPayingData;
										const updatedBill = {
											...bill,
											widgetData: {
												...bill.widgetData,
												'what-are-you-paying': {
													...existingData,
													zipCode: pendingZipCode,
												},
											},
										};
										onUpdateBill(updatedBill);
									});
									setPendingZipCode('');
								}}
							>
								Compare
							</Button>
						</Group>
						<Text size='xs' c='dimmed'>
							Bills: {billsMissingZip.map((b) => b.name).join(', ')}
						</Text>
					</Stack>
				) : (
					<Text size='sm' c='dimmed'>
						No bills shared yet. When adding or editing a bill, enable
						"Share with What Are You Paying" to see how your costs compare
						to others in your area.
					</Text>
				)}
			</Paper>
		);
	}

	return (
		<Paper withBorder p='md' radius='md'>
			<Group gap='sm' mb='xs'>
				<ThemeIcon size='lg' radius='md' variant='light' color='cyan'>
					<IconUsers size={20} />
				</ThemeIcon>
				<Text fw={600}>What Are You Paying</Text>
				<Badge size='xs' color='cyan'>
					{sharedBills.length} bill{sharedBills.length !== 1 ? 's' : ''}
				</Badge>
				{isLoading && <Loader size='xs' />}
			</Group>

			{/* Data source indicator */}
			<Group gap='xs' mb='md'>
				<Tooltip
					label={
						dataSource === 'official'
							? `Based on HUD Fair Market Rent data${dataYear ? ` (FY${dataYear})` : ''} for ZIP ${currentZipCode}`
							: 'Using national averages (ZIP-specific data loading or unavailable)'
					}
					multiline
					w={250}
				>
					<Group gap={4} style={{ cursor: 'help' }}>
						<IconBuilding size={12} color='var(--mantine-color-dimmed)' />
						<Text size='xs' c='dimmed'>
							{dataSource === 'official' ? (
								<>
									HUD data for {currentZipCode}
									{dataYear && ` (${dataYear})`}
								</>
							) : (
								'National averages'
							)}
						</Text>
						<IconInfoCircle size={12} color='var(--mantine-color-dimmed)' />
					</Group>
				</Tooltip>
			</Group>

			<Stack gap='md'>
				{Object.entries(categoryStats).map(([cat, stats]) => {
					const userAvg = stats.total / stats.count;
					const officialAvg = averages[cat as BillCategory] || 100;
					const comparison = ((userAvg - officialAvg) / officialAvg) * 100;

					const ComparisonIcon =
						comparison > 5
							? IconTrendingUp
							: comparison < -5
								? IconTrendingDown
								: IconMinus;

					const comparisonColor =
						comparison > 5 ? 'red' : comparison < -5 ? 'green' : 'gray';

					const categoryLabel =
						BILL_CATEGORIES.find((c) => c.value === cat)?.label || cat;

					return (
						<Stack key={cat} gap='xs'>
							<Group justify='space-between'>
								<Text size='sm' fw={500}>
									{categoryLabel}
								</Text>
								<Badge size='xs' variant='light'>
									{stats.count} bill{stats.count !== 1 ? 's' : ''}
								</Badge>
							</Group>

							<Group justify='space-between'>
								<Text size='xs' c='dimmed'>
									You pay: ${userAvg.toFixed(0)}
								</Text>
								<Tooltip
									label={
										dataSource === 'official'
											? 'Based on HUD Fair Market Rent data'
											: 'Based on national averages'
									}
								>
									<Text size='xs' c='dimmed' style={{ cursor: 'help' }}>
										Area avg: ${officialAvg.toFixed(0)}
									</Text>
								</Tooltip>
							</Group>

							<Progress
								value={Math.min(100, Math.max(0, 50 + comparison / 2))}
								color={comparisonColor}
								size='sm'
								radius='xl'
							/>

							<Group gap='xs' justify='center'>
								<ComparisonIcon
									size={14}
									color={`var(--mantine-color-${comparisonColor}-6)`}
								/>
								<Text size='xs' c={comparisonColor} fw={500}>
									{Math.abs(comparison).toFixed(0)}%{' '}
									{comparison > 0 ? 'above' : comparison < 0 ? 'below' : 'at'}{' '}
									area average
								</Text>
							</Group>
						</Stack>
					);
				})}
			</Stack>

			{/* Prompt for bills missing ZIP code */}
			{billsMissingZip.length > 0 && onUpdateBill && (
				<Alert
					icon={<IconMapPin size={16} />}
					color='yellow'
					variant='light'
					mt='md'
					p='sm'
				>
					<Stack gap='xs'>
						<Text size='sm'>
							{billsMissingZip.length} bill{billsMissingZip.length !== 1 ? 's' : ''} missing ZIP code
						</Text>
						<Group gap='xs'>
							<TextInput
								placeholder='ZIP code'
								value={pendingZipCode}
								onChange={(e) => {
									const cleaned = e.target.value.replace(/\D/g, '').slice(0, 5);
									setPendingZipCode(cleaned);
								}}
								size='xs'
								maxLength={5}
								styles={{
									input: { width: 80 },
								}}
							/>
							<Button
								size='xs'
								variant='light'
								disabled={pendingZipCode.length !== 5}
								onClick={() => {
									billsMissingZip.forEach((bill) => {
										const existingData = bill.widgetData?.[
											'what-are-you-paying'
										] as WhatAreYouPayingData;
										const updatedBill = {
											...bill,
											widgetData: {
												...bill.widgetData,
												'what-are-you-paying': {
													...existingData,
													zipCode: pendingZipCode,
												},
											},
										};
										onUpdateBill(updatedBill);
									});
									setPendingZipCode('');
								}}
							>
								Add to {billsMissingZip.length === 1 ? 'bill' : `all ${billsMissingZip.length}`}
							</Button>
						</Group>
						<Text size='xs' c='dimmed'>
							Bills: {billsMissingZip.map((b) => b.name).join(', ')}
						</Text>
					</Stack>
				</Alert>
			)}

			<Text size='xs' c='dimmed' ta='center' mt='md'>
				{dataSource === 'official'
					? 'Averages from HUD Fair Market Rent data'
					: primaryZipCode
						? 'Using national averages (local data unavailable)'
						: 'Add your ZIP code to get local averages'}
			</Text>
		</Paper>
	);
}

export default WhatAreYouPaying;
